import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axiosConfig';
import moment from 'moment';

import 'react-quill/dist/quill.snow.css';
import _ from 'lodash';
import BigFiveTestResultDisplay from '../components/BigFiveTestResultDisplay';
// emoji png
import emoji1 from '../assets/emojis/1.svg';
import emoji2 from '../assets/emojis/2.svg';
import emoji3 from '../assets/emojis/3.svg';
import emoji4 from '../assets/emojis/4.svg';
import emoji5 from '../assets/emojis/5.svg';
import emoji6 from '../assets/emojis/6.svg';
import emoji7 from '../assets/emojis/7.svg';

import UserProfileCard from '../components/profile/UserProfileCard';
import UserStats from '../components/profile/UserStats';
import UserMindStatus from '../components/profile/UserMindStatus';
import UserTestHistory from '../components/profile/UserTestHistory';

interface User {
  userId: string;
  displayName: string;
  fullName: string;
  email: string;
  referralCode?: string;
}

interface OnboardingData {
  emotionalState: string[];
  personality: string[];
  trauma: string[];
}

interface TestInfo {
  id: string;
  title: string;
  description: string;
  path: string;
}

interface LatestTestResults {
  [testType: string]: {
    createdAt: string;
  };
}

const tests: TestInfo[] = [
  {
    id: 'phq-9',
    title: '우울증 자가 진단 (PHQ-9)',
    description: '지난 2주 동안의 기분 상태를 점검하여 우울 증상 수준을 확인합니다.',
    path: '/tests/phq-9',
  },
  {
    id: 'gad-7',
    title: '불안 척도 (GAD-7)',
    description: '지난 2주 동안의 불안 증상을 점검하여 불안 수준을 확인합니다.',
    path: '/tests/gad-7',
  },
  {
    id: 'pss',
    title: '스트레스 자가 척도 (PSS)',
    description: '지난 한 달간 경험한 스트레스 수준을 평가합니다.',
    path: '/tests/pss',
  },
  {
    id: 'rses',
    title: '자존감 척도 (RSES)',
    description: '자신에 대한 긍정적, 부정적 평가를 통해 전반적인 자존감 수준을 측정합니다.',
    path: '/tests/rses',
  },
  {
    id: 'pcl-5',
    title: '외상 후 스트레스 척도 (PCL-5)',
    description: '충격적인 사건 경험 후 겪을 수 있는 스트레스 반응을 평가합니다.',
    path: '/tests/pcl-5',
  },
  {
    id: 'bdi',
    title: '벡 우울척도 (BDI)',
    description:
      '우울증의 인지적, 정서적, 신체적 증상을 포함한 21개 문항을 통해 우울 수준을 측정합니다.',
    path: '/tests/pcl-5',
  },
];

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [latestResults, setLatestResults] = useState<LatestTestResults>({});
  const [avgEmotion, setAvgEmotion] = useState<number | null>(null);
  const [counselingCount, setCounselingCount] = useState<number | null>(null);
  const [consecutiveDays, setConsecutiveDays] = useState<number | null>(null);
  const [dailyJournal, setDailyJournal] = useState<{ content: string; emotion: number } | null>(
    null
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      await axios.post('/api/users/save-counseling-summary');
      console.log('상담 요약이 성공적으로 저장되었습니다.');

      // 2. PDF 보고서 다운로드 요청
      const response = await axios.get('/api/users/report', {
        responseType: 'blob',
        timeout: 120000,
      });

      console.log(response, '=================================response');

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '리본톡_심리상담_결과지.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log('엥?');
      console.error('PDF 보고서 생성 또는 다운로드에 실패했습니다.', error);
      alert('보고서를 생성하는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        try {
          const response = await axios.get('/api/users/get', { params: { id: parsedUserData.id } });
          setUser(response.data);
        } catch (error) {
          console.error('Failed to fetch user data:', error);
        }
      }
    };

    const fetchOnboardingData = async () => {
      try {
        const response = await axios.get('/api/profiles/me');
        setOnboardingData(response.data);
      } catch (error) {
        console.error('Failed to fetch onboarding data:', error);
      }
    };

    const fetchLatestTestResults = async () => {
      try {
        const response = await axios.get<LatestTestResults>('/api/test-results/latest-by-type');
        setLatestResults(response.data);
      } catch (error) {
        console.error('Failed to fetch latest test results:', error);
      }
    };

    const fetchStats = async () => {
      try {
        const [avgRes, countRes, consecutiveRes] = await Promise.all([
          axios.get('/api/emotions/average'),
          axios.get('/api/counseling/count'),
          axios.get('/api/emotions/consecutive-days'),
        ]);

        setAvgEmotion(avgRes.data?.average ?? null);
        setCounselingCount(countRes.data?.count ?? 0);
        setConsecutiveDays(consecutiveRes.data?.consecutiveDays ?? 0);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };

    const fetchDailyJournal = async () => {
      try {
        const response = await axios.get('/api/daily-journals/today-content');
        setDailyJournal(response.data);
      } catch (error) {
        console.error('Failed to fetch daily journal:', error);
      }
    };

    fetchUserData();
    fetchOnboardingData();
    fetchLatestTestResults();
    fetchStats();
    fetchDailyJournal();
  }, []);

  const renderTags = (tags: string[] | undefined) => {
    if (!tags || tags.length === 0) return <p>아직 기록된 정보가 없습니다.</p>;
    return (
      <div className="tags-container">
        {tags.map(tag => (
          <span key={tag} className="tag">
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const formatLastTestedDate = (testId: string) => {
    const result = latestResults[testId];
    if (result && _.get(result, 'createdAt')) {
      return `최근 검사: ${moment(result.createdAt).format('YYYY.MM.DD HH:mm')}`;
    }
    return '아직 검사 기록이 없습니다.';
  };

  const getEmoji = (emotion: number) => {
    const emotionMap = {
      1: emoji1,
      2: emoji2,
      3: emoji3,
      4: emoji4,
      5: emoji5,
      6: emoji6,
      7: emoji7,
    };
    return emotionMap[emotion as keyof typeof emotionMap] || emoji7;
  };

  return (
    <div className="main-container">
      <div className="setting-container app-container">
        <UserProfileCard user={user} />

        <button
          className="register-counselor-btn"
          onClick={() => navigate('/profile/register-counselor')}
        >
          상담사 등록 신청
        </button>

        <UserStats
          avgEmotion={avgEmotion}
          counselingCount={counselingCount}
          consecutiveDays={consecutiveDays}
          isDownloading={isDownloading}
          handleDownloadReport={handleDownloadReport}
        />

        <UserMindStatus
          onboardingData={onboardingData}
          dailyJournal={dailyJournal}
          getEmoji={getEmoji}
          renderTags={renderTags}
        />

        <section className="setting-section">
          <div className="section-header">
            <h2>Big-Five 성격 검사 결과</h2>
          </div>
          <BigFiveTestResultDisplay />
        </section>

        <UserTestHistory
          tests={tests}
          latestResults={latestResults}
          formatLastTestedDate={formatLastTestedDate}
        />
      </div>
    </div>
  );
};

export default ProfilePage;
